import { FunctionComponent, useEffect } from 'react';
import { Stack, FormControl, InputLabel, OutlinedInput, FormHelperText, Select, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import {
  IFriendlyCategory,
  IFriendlyCategoryGroup,
  IPostFriendlyCategory,
} from '../../../shared/services/super-admin/super-admin.service';

export const AddEditFriendlyCategoryDialog: FunctionComponent<{
  open: boolean;
  closeHandler: () => void;
  isEdit: boolean;
  category?: IFriendlyCategory;
  groups: IFriendlyCategoryGroup[];
  isSubmitting: boolean;
  onSubmit: (values: IPostFriendlyCategory, resetForm: () => void, isEdit: boolean) => void;
}> = ({ open, closeHandler, isEdit, category, groups, isSubmitting, onSubmit }) => {
  const schema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required'),
    friendlyCategoryGroupId: Yup.string().required('Group is required'),
    icon: Yup.string().nullable(),
    description: Yup.string().nullable(),
    displayOrder: Yup.number().nullable(),
  });

  const formik = useFormik<IPostFriendlyCategory>({
    validationSchema: schema,
    enableReinitialize: false,
    initialValues: {
      name: '',
      friendlyCategoryGroupId: '',
      icon: '',
      description: '',
      displayOrder: 0,
    },
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm, isEdit);
    },
  });

  useEffect(() => {
    if (open && isEdit && category) {
      formik.setFieldValue('name', category.name);
      formik.setFieldValue('friendlyCategoryGroupId', category.friendlyCategoryGroupId);
      formik.setFieldValue('icon', category.icon ?? '');
      formik.setFieldValue('description', category.description ?? '');
      formik.setFieldValue('displayOrder', category.displayOrder ?? 0);
    } else if (!open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={isEdit ? 'Edit Category' : 'Add Category'}
      cancelHandler={closeHandler}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}
      disableSaveButton={isSubmitting}
    >
      <Stack sx={{ mt: 1 }}>
        <FormControl fullWidth error={formik.touched.name && Boolean(formik.errors.name)}>
          <InputLabel htmlFor="name">Name</InputLabel>
          <OutlinedInput
            id="name"
            label="Name"
            value={formik.values.name || ''}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
          />
          <FormHelperText>
            {formik.touched.name && formik.errors.name ? formik.errors.name : ' '}
          </FormHelperText>
        </FormControl>
        <FormControl
          fullWidth
          error={formik.touched.friendlyCategoryGroupId && Boolean(formik.errors.friendlyCategoryGroupId)}
        >
          <InputLabel id="group-label">Group</InputLabel>
          <Select
            labelId="group-label"
            id="friendlyCategoryGroupId"
            value={formik.values.friendlyCategoryGroupId || ''}
            label="Group"
            onChange={(e) => formik.setFieldValue('friendlyCategoryGroupId', e.target.value)}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {formik.touched.friendlyCategoryGroupId && formik.errors.friendlyCategoryGroupId
              ? formik.errors.friendlyCategoryGroupId
              : ' '}
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel htmlFor="icon">Icon (MUI icon name)</InputLabel>
          <OutlinedInput
            id="icon"
            label="Icon (MUI icon name)"
            value={formik.values.icon || ''}
            onChange={(e) => formik.setFieldValue('icon', e.target.value)}
          />
          <FormHelperText> </FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel htmlFor="description">Description</InputLabel>
          <OutlinedInput
            id="description"
            label="Description"
            value={formik.values.description || ''}
            onChange={(e) => formik.setFieldValue('description', e.target.value)}
            multiline
            rows={2}
          />
          <FormHelperText> </FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel htmlFor="displayOrder">Display Order</InputLabel>
          <OutlinedInput
            id="displayOrder"
            label="Display Order"
            type="number"
            value={formik.values.displayOrder ?? ''}
            onChange={(e) => formik.setFieldValue('displayOrder', parseInt(e.target.value) || 0)}
          />
          <FormHelperText> </FormHelperText>
        </FormControl>
      </Stack>
    </DialogWrapper>
  );
};
